import React, { useState } from 'react';
import { ArrowRightIcon, CheckIcon, FlagIcon, TimerIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDatos } from '../../contexts/DatosContext';
import { estiloColor } from '../../data/flujos';
import { abogadoPorId } from '../../data/abogados';
import type { Expediente } from '../../types';
import { fechaCorta, fechaRel } from '../../utils/fechas';
import { EstadoVacio } from '../ui/Basicos';
import { MarcaGoogle } from '../ui/Google';
import { etiquetaAnticipacion } from '../../utils/google';
import { addDays, format, parseISO } from 'date-fns';
export function LineaTiempoEtapas({
  expediente
}: {
  expediente: Expediente;
}) {
  const {
    etapasDe,
    avanzarEtapa,
    rol,
    conexionGoogleDe
  } = useDatos();
  const etapas = etapasDe(expediente.materia);
  const siguiente = etapas[expediente.etapaActual + 1];
  const [avanzando, setAvanzando] = useState(false);
  const [fecha, setFecha] = useState('');
  const [responsableId, setResponsableId] = useState(expediente.abogadoIds[0]);
  const apertura = parseISO(expediente.fechaApertura);
  if (etapas.length === 0) {
    return <EstadoVacio mensaje={`La materia ${expediente.materia} todavía no tiene un flujo configurado`} sugerencia="Un administrador puede definir sus etapas en Configuración → Flujos de proceso." data-id="element-483" data-mp="11fu3330udf4x" />;
  }
  const abrirAvance = () => {
    setFecha(siguiente?.tienePlazo ? fechaRel(siguiente.diasSugeridos) : '');
    setResponsableId(expediente.abogadoIds[0]);
    setAvanzando(true);
  };
  const confirmar = () => {
    avanzarEtapa(expediente.id, expediente.etapaActual + 1, siguiente?.tienePlazo && fecha ? {
      concepto: `${siguiente.nombre} · término de ${siguiente.diasSugeridos} días`,
      fechaLimite: fecha,
      responsableId
    } : undefined);
    setAvanzando(false);
  };
  return <div className="px-5 py-6" data-id="element-484" data-mp="22ynspetk6jt1">
      <ol className="relative" data-id="element-485" data-mp="22xcm778qaxy8">
        {etapas.map((etapa, i) => {
        const completada = i < expediente.etapaActual;
        const actual = i === expediente.etapaActual;
        const color = estiloColor(etapa.color);
        const fechaEtapa = format(addDays(apertura, i * 34), 'yyyy-MM-dd');
        return <li key={etapa.id} className="relative flex gap-4 pb-6 last:pb-0" data-id="element-486" data-mp="2jxfjqkkakyk4" data-mpi={i}>
              {i < etapas.length - 1 && <span className={`absolute left-[15px] top-8 h-full w-0.5 ${completada ? 'bg-marino-700' : 'bg-gris-200'}`} aria-hidden="true" data-id="element-487" data-mp="1rb35nd3uptsa" />}
              <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-2xs font-bold ${completada ? 'bg-marino-700 text-white' : actual ? `${color.punto} text-white ring-4 ring-marino-50` : 'border border-gris-300 bg-white text-gris-400'}`} data-id="element-488" data-mp="217fwm380htjc">
                {completada ? <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-489" data-mp="11014fss0ifx3" /> : i + 1}
              </span>

              <div className="min-w-0 flex-1 pt-1" data-id="element-490" data-mp="w0y6sted3bz5">
                <div className="flex flex-wrap items-center gap-2" data-id="element-491" data-mp="vzmvm8il71y0">
                  <p className={`text-sm ${actual ? 'font-semibold text-marino-900' : 'font-medium text-gris-700'}`} data-id="element-492" data-mp="r1cznvp1fxbc">
                    {etapa.nombre}
                  </p>
                  {etapa.etiqueta && <span className={`inline-flex items-center rounded px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide ${color.chip}`} data-id="element-493" data-mp="dc1a6ivhs7dr">
                      {etapa.etiqueta}
                    </span>}
                  {actual && <span className="rounded bg-marino-800 px-1.5 py-0.5 text-2xs font-bold uppercase tracking-wide text-white" data-id="element-494" data-mp="3vhw5eywadox2">
                      Etapa actual
                    </span>}
                  {etapa.esHito && <span className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-wide text-ambar-700" data-id="element-495" data-mp="f9j63xucr9p5">
                      <FlagIcon className="h-3 w-3" aria-hidden="true" data-id="element-496" data-mp="ja32gz73q9a8" />
                      Hito
                    </span>}
                </div>

                {etapa.descripcion && <p className="mt-1 text-xs text-gris-600" data-id="element-497" data-mp="ab9axn8mo93j">{etapa.descripcion}</p>}

                <p className="tabular mt-1 text-2xs text-gris-500" data-id="element-498" data-mp="3she6oks3fg6s">
                  {completada ? `Concluida el ${fechaCorta(fechaEtapa)}` : actual ? `En curso desde el ${fechaCorta(fechaEtapa)}` : 'Pendiente'}
                  {etapa.tienePlazo && ` · plazo sugerido de ${etapa.diasSugeridos} días`}
                </p>

                {etapa.documentosEsperados.length > 0 && <div className="mt-2" data-id="element-499" data-mp="3b7a9hiqp09u6">
                    <p className="text-2xs uppercase tracking-wide text-gris-400" data-id="element-500" data-mp="3b8bo1x6x3egn">
                      Documentos esperados
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1.5" data-id="element-501" data-mp="3k7ov6j7kjqy7">
                      {etapa.documentosEsperados.map(d => <span key={d} className="rounded bg-gris-100 px-1.5 py-0.5 text-2xs text-gris-600" data-id="element-502" data-mp="2pm7noh6h48i">
                          {d}
                        </span>)}
                    </div>
                  </div>}
              </div>
            </li>;
      })}
      </ol>

      <div className="mt-2 border-t border-gris-200 pt-4" data-id="element-503" data-mp="2djrqlf3bhrcg">
        {!siguiente ? <p className="text-xs text-gris-500" data-id="element-504" data-mp="28kotqhxhqrfk">
            El expediente se encuentra en la última etapa del flujo de {expediente.materia}.
          </p> : rol === 'Recepción/Archivo' ? <p className="text-xs text-gris-500" data-id="element-505" data-mp="hf29oo8r2ibf">
            Siguiente etapa: <span className="font-medium text-gris-700" data-id="element-506" data-mp="meybqhxvzzoc">{siguiente.nombre}</span>.
            Solo Administrador o Abogado pueden avanzar la etapa.
          </p> : !avanzando ? <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-507" data-mp="qe75vnwu9zkf">
            <p className="text-xs text-gris-500" data-id="element-508" data-mp="vfczdnybpqv0">
              Siguiente etapa:{' '}
              <span className="font-medium text-gris-700" data-id="element-509" data-mp="10d5097gy4nbc">{siguiente.nombre}</span>
              {siguiente.tienePlazo && ` · se propondrá un vencimiento a ${siguiente.diasSugeridos} días`}
            </p>
            <button type="button" onClick={abrirAvance} className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-510" data-mp="1a6lta999dydy">
              Avanzar a «{siguiente.nombre}»
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-511" data-mp="2yo585c9ctwdu" />
            </button>
          </div> : <div className="rounded-lg border border-marino-300 bg-marino-50 px-4 py-4" data-id="element-512" data-mp="2um1ucgz7qfpe">
            <p className="text-sm font-semibold text-marino-900" data-id="element-513" data-mp="2pnqkw7cty77p">
              Avanzar a «{siguiente.nombre}»
            </p>
            {siguiente.esHito && <p className="mt-1 flex items-center gap-1.5 text-2xs font-semibold text-ambar-700" data-id="element-514" data-mp="3mcy9xpcnqygd">
                <FlagIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-515" data-mp="3ie8wjwrmdgwq" />
                Etapa marcada como hito: se generará una alerta automática al despacho.
              </p>}

            {siguiente.tienePlazo ? <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-516" data-mp="9w2ocd1qagpf">
                <label data-id="element-517" data-mp="9xnjo4vva3oc">
                  <span className="text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-518" data-mp="55ma4qq32r20">
                    Fecha de vencimiento propuesta
                  </span>
                  <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className="tabular mt-1.5 h-9 w-full rounded border border-gris-200 px-3 text-sm text-gris-800" data-id="element-519" data-mp="39m4comj0vdec" />
                  <span className="mt-1 block text-2xs text-gris-500" data-id="element-520" data-mp="34ovvao5p7de5">
                    Calculada con los {siguiente.diasSugeridos} días configurados en el flujo. Es
                    editable.
                  </span>
                </label>
                <label data-id="element-521" data-mp="3jdig35e3gxcr">
                  <span className="text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-522" data-mp="3jc73cg2o8f9i">
                    Responsable del término
                  </span>
                  <select value={responsableId} onChange={e => setResponsableId(e.target.value)} className="mt-1.5 h-9 w-full rounded border border-gris-200 px-2.5 text-sm text-gris-800" data-id="element-523" data-mp="27ybnt67uu4hr">
                    {expediente.abogadoIds.map(id => <option key={id} value={id} data-id="element-524" data-mp="1zg1b21p3st00">
                        {abogadoPorId(id)?.nombre}
                      </option>)}
                  </select>
                  {conexionGoogleDe(responsableId).estado === 'conectado' ? <span className="mt-1 flex items-center gap-1.5 text-2xs text-gris-500" data-id="element-525" data-mp="1lpo4a2ny7kc9">
                      <MarcaGoogle titulo="Google Calendar conectado" data-id="element-526" data-mp="1lnbmbpi6x9z7" />
                      El término se enviará a {conexionGoogleDe(responsableId).correoGoogle} con
                      recordatorio{' '}
                      {etiquetaAnticipacion(conexionGoogleDe(responsableId).anticipacionMinutos)}.
                    </span> : <span className="mt-1 block text-2xs text-ambar-700" data-id="element-527" data-mp="12xvk8xqux9go">
                      Esta persona no tiene Google Calendar conectado, no recibirá la notificación
                      de Google.{' '}
                      <Link to={`/abogados/${responsableId}`} className="font-semibold underline" data-id="element-528" data-mp="2zs5zz45gcli0">
                        Conectar cuenta
                      </Link>
                    </span>}
                </label>
              </div> : <p className="mt-2 text-xs text-gris-600" data-id="element-529" data-mp="2gtlathn4fq70">
                Esta etapa no tiene plazo asociado, por lo que no se generará un término.{' '}
                <Link to="/configuracion/flujos" className="font-semibold text-marino-700 hover:underline" data-id="element-530" data-mp="2c1jy5um1jxgg">
                  Configurar el flujo
                </Link>
              </p>}

            <div className="mt-4 flex items-center gap-2" data-id="element-531" data-mp="1y3vgswr6n8g5">
              <button type="button" onClick={confirmar} className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-532" data-mp="1y5g9shxumjbw">
                <TimerIcon className="h-4 w-4" aria-hidden="true" data-id="element-533" data-mp="1nxu7kh34keg2" />
                Confirmar avance
              </button>
              <button type="button" onClick={() => setAvanzando(false)} className="h-9 rounded border border-gris-200 bg-white px-3 text-sm font-medium text-gris-600 transition-colors duration-150 hover:border-gris-300" data-id="element-534" data-mp="1swfbaqhl5g99">
                Cancelar
              </button>
            </div>
          </div>}
      </div>
    </div>;
}